import { useMemo } from "react";
import { bool, func, node, number, oneOf, oneOfType, shape, string } from "prop-types";
import { ClearInput } from "@components";
import { Tooltip } from "@material-ui/core";
import { CellInputReadonlyValue } from "./CellInputReadonlyValue";
import { makeStyles } from "@material-ui/styles";
import classnames from "classnames";

export const CellInput = ({
  value,
  valueType,
  onValueChange,
  editable,
  emptyInputs,
  
  tooltipMessage,
  messageTitle,
  messageContent,
  
  symbolStart,
  symbolEnd,
  americanFormat,
  applySuffixSpacing,
  decimals=2,
  showDecimals,
  fixedDecimalScale,
  showValueSign,
  placeholder,
  
  warning,
  alignContent,
  className,
  inputClassName,
  applyReadonlyPadding,
  fullWidthInputs,
  halfWidth,
  ...valueProps
}) => {
  const classes = useStyle();
  
  const prefix = useMemo(() => (
    value !== 0 && showValueSign
      ? (value < 0 ? "-" : "+") + (symbolStart || '')
      : symbolStart
  ), [ value, showValueSign, symbolStart ]);
  
  let content = editable
    ? (
      <ClearInput
        {...valueProps}
        alignContent={alignContent}
        americanFormat={americanFormat}
        applySuffixSpacing={applySuffixSpacing}
        showDecimals={showDecimals}
        fixedDecimalScale={fixedDecimalScale}
        decimals={decimals}
        color={warning ? "warning" : "primary"}
        value={emptyInputs ? undefined : value}
        prefix={emptyInputs ? undefined : prefix}
        suffix={emptyInputs ? undefined : symbolEnd}
        placeholder={emptyInputs ? undefined : placeholder}
        tabIndex={messageContent ? -1 : undefined}
        className={classnames(`input-${valueType}`, className, inputClassName)}
        containerClassName={classnames( halfWidth && classes.halfInputWidth)}
        onChange={onValueChange}
        clickDialogMessageContent={messageContent}
        clickDialogMessageTitle={messageTitle}
        fullWidth={fullWidthInputs}
        placeholderAsCopiedValue
      />
    )
    : <CellInputReadonlyValue
      value={typeof value === "undefined" || emptyInputs ? "" : value}
      prefix={emptyInputs ? undefined : prefix}
      suffix={emptyInputs ? undefined : applySuffixSpacing && symbolEnd ? ` ${symbolEnd.trim()}` : symbolEnd}
      decimalScale={showDecimals || fixedDecimalScale ? decimals : 0}
      fixedDecimalScale={fixedDecimalScale}
      americanFormat={americanFormat}
      className={classnames(
        halfWidth && classes.halfInputWidth,
        applyReadonlyPadding && "px-1-5 py-1-5"
      )}
      alignContent={alignContent}
    />
  
  return tooltipMessage
    ? <Tooltip
      title={ tooltipMessage }
      disableHoverListener={ !tooltipMessage }
      disableFocusListener
      disableTouchListener
    >
      { content }
    </Tooltip>
    : content;
}


CellInput.propTypes = {
  value: oneOfType([number, string]),
  valueType: oneOf(["min", "avg", "max"]),
  onValueChange: func,
  onFocus: func,
  onBlur: func,
  editable: bool,
  emptyInputs: bool,
  
  symbolStart: string,
  symbolEnd: oneOfType([node, string]),
  tooltipMessage: oneOfType([string, node]),
  
  min: oneOfType([number, shape({ min: number, max: number })]),
  max: oneOfType([number, shape({ min: number, max: number })]),
  
  placeholder: oneOfType([string, number]),
  showDecimals: bool,
  fixedDecimalScale: bool,
  showValueSign: bool,
  allowNegative: bool,
  messageTitle: string,
  messageContent: string,
  decimals: number,
  navigationKey: string,
  americanFormat: bool,
  applySuffixSpacing: bool,
  
  warning: bool,
  halfWidth: bool,
  fullWidthInputs: bool,
  className: string,
  inputClassName: string,
  applyReadonlyPadding: bool,
  size: oneOf(["small", "large"]),
  alignContent: oneOf(["left", "center", "right"]),
};

const useStyle = makeStyles(() => ({
  halfInputWidth: {
    width: "calc(50% - 8px)"
  }
}));